/**
 * QuickJoinDialog — one-shot fitting suggestion modal triggered from a tray
 * right-click ("Suggest fitting to nearest tray").  Auto-picks the closest
 * other tray, shows the top SKU + routing hints, lets the user apply with one
 * button click — or pick a different SKU from the list.
 *
 * Calls:
 *   POST /api/projects/{pid}/trays/{srcId}/quick-join     → {partner, candidates, joint}
 *   POST /api/projects/{pid}/trays/fitting/join           → applies the join
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Lightning } from "@phosphor-icons/react";
import FittingIcon from "@/components/trays/FittingIcon";

const FITTING_LABELS = {
  horizontal_elbow_90: "90° elbow",
  horizontal_elbow_45: "45° elbow",
  vertical_elbow_90: "Vertical 90°",
  vertical_elbow_45: "Vertical 45°",
  reducer: "Reducer", dropout: "Dropout",
  horizontal_tee: "Tee", horizontal_cross: "Cross",
};

export default function QuickJoinDialog({ open, sourceSeg, projectId, onClose, onJoined }) {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);   // {partner, candidates, joint}
  const [pickedSku, setPickedSku] = useState("");
  const [verticalBend, setVerticalBend] = useState(false);
  /** User-supplied alternative SKU + free-text routing note. When the AI
   *  suggestions aren't quite right the user can type any SKU here and
   *  it overrides `pickedSku` at apply time. The note is persisted on
   *  the resulting fitting's metadata so reviewers see the rationale. */
  const [customSku, setCustomSku] = useState("");
  const [customNote, setCustomNote] = useState("");
  const [customOpen, setCustomOpen] = useState(false);

  useEffect(() => {
    if (!open || !sourceSeg) return undefined;
    let cancelled = false;
    setData(null); setPickedSku(""); setBusy(true);
    (async () => {
      try {
        const { data: d } = await api.post(
          `/projects/${projectId}/trays/${sourceSeg.id}/quick-join`);
        if (cancelled) return;
        setData(d);
        setPickedSku(d.candidates?.[0]?.sku || "");
      } catch (e) {
        if (!cancelled) {
          toast.error(e?.response?.data?.detail || "No nearby tray found");
          onClose && onClose();
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, sourceSeg, projectId, onClose]);

  if (!open || !sourceSeg) return null;

  const apply = async () => {
    const finalSku = customSku.trim() || pickedSku;
    if (!finalSku || !data?.partner) return;
    setBusy(true);
    try {
      const { data: out } = await api.post(
        `/projects/${projectId}/trays/fitting/join`,
        { seg_a_id: sourceSeg.id, seg_b_id: data.partner.id,
          fitting_sku: finalSku, vertical_bend: verticalBend,
          user_note: customNote.trim() || undefined },
      );
      onJoined && onJoined(out.segments || []);
      const fitLabel = FITTING_LABELS[out.fitting?.fitting_type] || "fitting";
      const noteHint = customNote.trim() ? " · note saved" : "";
      toast.success(`Joined with ${fitLabel} (${finalSku})${noteHint}`);
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Join failed");
    } finally {
      setBusy(false);
    }
  };

  const srcLabel = sourceSeg.id_display || sourceSeg.id?.slice(0, 6);
  const partner = data?.partner;
  const joint   = data?.joint;
  const top     = data?.candidates?.[0];

  return (
    <div className="fixed inset-0 z-[1500] bg-black/70 flex items-center justify-center p-4"
         onClick={onClose} data-testid="quick-join-dialog">
      <div className="bg-[#0a0d12] border border-[#FFCC00]/40 w-full max-w-2xl p-5 font-mono"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-tag">// Quick join · suggest fitting to nearest tray</div>
            <div className="text-base text-white inline-flex items-center gap-2 mt-1">
              <Lightning size={16} className="text-[#FFCC00]" />
              From <span className="text-[#00E5FF]">{srcLabel}</span>
              {partner && (<>
                <span className="text-gray-500">→</span>
                <span className="text-[#00E5FF]">{partner.id_display || partner.id.slice(0, 6)}</span>
                <span className="text-[10px] text-gray-500">
                  {partner.distance_in}″ away · {partner.width_in}″ {partner.material}
                </span>
              </>)}
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost p-1" data-testid="quick-join-close">
            <X size={14} />
          </button>
        </div>

        {busy && !data && (
          <div className="text-xs text-gray-400 py-6 text-center">
            Finding nearest tray and computing fitting candidates…
          </div>
        )}

        {data && !top && (
          <div className="text-xs text-yellow-300 py-4">
            No catalog fitting matched this joint — open the full JoinTraysPanel
            to choose manually.
          </div>
        )}

        {data && top && (
          <div className="space-y-3">
            <div>
              <div className="label-tag mb-1 text-[10px]">
                Routing suggestions ({data.candidates.length}) — pick one
              </div>
              <div className="max-h-56 overflow-y-auto border border-white/10 divide-y divide-white/5"
                   data-testid="quick-join-suggestions">
                {data.candidates.map((f) => {
                  const isPicked = pickedSku === f.sku;
                  return (
                    <button key={f.sku}
                            type="button"
                            onClick={() => setPickedSku(f.sku)}
                            className={`w-full text-left p-2 font-mono text-[11px] transition-colors flex items-start gap-2 ${
                              isPicked
                                ? "bg-[#FFCC00]/15 border-l-2 border-[#FFCC00]"
                                : "hover:bg-white/5 border-l-2 border-transparent"
                            }`}
                            data-testid={`quick-join-option-${f.sku}`}>
                      <div className="shrink-0 mt-0.5">
                        <FittingIcon kind={f.fitting_type} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={isPicked ? "text-[#FFCC00]" : "text-[#00E5FF]"}>
                            {f.sku}
                          </span>
                          <span className="text-gray-400">${f.list_price_usd}</span>
                        </div>
                        <div className="text-gray-400 truncate">{f.label}</div>
                        <div className="text-[10px] text-gray-500">
                          {f.match_score >= 10
                            ? "same series ✓"
                            : f.match_score >= 5
                              ? "width match"
                              : "generic"}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {joint && (
              <div className="text-[10px] text-gray-500 font-mono">
                Joint: <span className="text-[#FFCC00]">{joint.angle_deg}°</span>
                {joint.is_vertical ? " · vertical" : " · horizontal"} ·
                corner ({joint.corner[0].toFixed(0)}, {joint.corner[1].toFixed(0)})
              </div>
            )}

            {joint?.routing_hints?.length > 0 && (
              <div className="border-l-2 border-[#FFCC00] bg-[#FFCC00]/10 pl-3 py-2 text-[11px] text-[#FFE16B]"
                   data-testid="quick-join-hints">
                <div className="text-[#FFCC00] mb-1">// routing suggestions</div>
                <ul className="list-disc pl-4 space-y-0.5">
                  {joint.routing_hints.map((h, i) => <li key={i}>{h}</li>)}
                </ul>
              </div>
            )}

            <label className="inline-flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
              <input type="checkbox" checked={verticalBend}
                     onChange={(e) => setVerticalBend(e.target.checked)}
                     className="accent-[#00E5FF]"
                     data-testid="quick-join-vertical-bend" />
              Vertical bend (use when one leg is a drop)
            </label>

            {/* User-suggested custom fitting — overrides the AI suggestion
                at apply time. Useful when the catalog match is wrong or
                the user wants to standardize on a specific SKU. */}
            <div className="border border-dashed border-[#00E5FF]/30 p-2">
              <button type="button"
                      onClick={() => setCustomOpen((v) => !v)}
                      className="w-full text-left text-[10px] text-[#00E5FF] hover:text-[#FFCC00] uppercase tracking-wider"
                      data-testid="quick-join-custom-toggle">
                {customOpen ? "− Hide" : "+ Suggest your own"} · custom SKU / routing note
              </button>
              {customOpen && (
                <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="quick-join-custom-form">
                  <label className="flex flex-col gap-1">
                    <span className="text-[9px] text-gray-500">Override SKU (overrides AI pick)</span>
                    <input type="text" value={customSku}
                           onChange={(e) => setCustomSku(e.target.value)}
                           placeholder="e.g. HOFFMAN-CTH-90L-12"
                           className="bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono focus:border-[#00E5FF] outline-none"
                           data-testid="quick-join-custom-sku" />
                  </label>
                  <label className="flex flex-col gap-1 md:col-span-2">
                    <span className="text-[9px] text-gray-500">Routing note (persisted with the joint)</span>
                    <input type="text" value={customNote}
                           onChange={(e) => setCustomNote(e.target.value)}
                           placeholder="e.g. 'Use long-radius elbow — short-radius fouls panel CT-302.'"
                           className="bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono focus:border-[#00E5FF] outline-none"
                           data-testid="quick-join-custom-note" />
                  </label>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose}
                      className="btn-ghost text-sm py-2"
                      data-testid="quick-join-cancel">
                Cancel
              </button>
              <button onClick={apply}
                      disabled={busy || (!pickedSku && !customSku.trim())}
                      className="btn-primary text-sm py-2 inline-flex items-center gap-2 disabled:opacity-40"
                      data-testid="quick-join-apply">
                {busy ? "Applying…" : customSku.trim() ? "Apply custom" : "Snap & join"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
